import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Prohibit, ArrowClockwise, CircleNotch, PlusCircle, CheckCircle, ShieldCheck,
    ClipboardText, PhoneSlash, CheckSquare, MagnifyingGlass, User, HandWaving, Clock,
} from "@phosphor-icons/react";

const SF_MODES = [
    { value: "off", label: "Don't touch Salesforce", hint: "Keep opt-outs only inside Inspiro.", icon: Prohibit },
    { value: "task", label: "Log a Salesforce Task", hint: "Non-destructive audit trail on the Lead/Contact.", icon: ClipboardText },
    { value: "donotcall", label: "Set DoNotCall = true", hint: "Standard field, cleared on re-subscribe.", icon: PhoneSlash },
    { value: "field", label: "Set a custom checkbox field", hint: "You provide the API name; toggled on opt-out.", icon: CheckSquare },
];

function within7Days(iso) {
    if (!iso) return false;
    return Date.now() - new Date(iso).getTime() < 7 * 864e5;
}

function Stat({ icon: Icon, value, label, tint, testid }) {
    return (
        <div data-testid={testid} className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4">
            <div className={`grid h-10 w-10 place-items-center rounded-xl ${tint}`}><Icon size={20} weight="duotone" /></div>
            <div>
                <div className="font-heading text-2xl font-bold leading-none">{value}</div>
                <div className="mt-1 text-xs font-medium text-stone2">{label}</div>
            </div>
        </div>
    );
}

export default function WhatsAppOptouts() {
    const [mode, setMode] = useState("off");
    const [savedMode, setSavedMode] = useState("off");
    const [field, setField] = useState("");
    const [savingCfg, setSavingCfg] = useState(false);
    const [list, setList] = useState(null);
    const [q, setQ] = useState("");
    const [newContact, setNewContact] = useState("");
    const [adding, setAdding] = useState(false);

    const loadCfg = async () => {
        try {
            const { data } = await api.get("/whatsapp/optout-config");
            setMode(data.mode || "off"); setSavedMode(data.mode || "off"); setField(data.field || "");
        } catch (e) { setMode("off"); }
    };
    const loadList = async () => {
        try { const { data } = await api.get("/whatsapp/optouts"); setList(data.optouts || []); }
        catch (e) { setList([]); toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load opt-outs"); }
    };
    useEffect(() => { loadCfg(); loadList(); }, []);

    const saveCfg = async () => {
        setSavingCfg(true);
        try { await api.put("/whatsapp/optout-config", { mode, field }); toast.success("Opt-out sync saved"); setSavedMode(mode); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not save"); }
        finally { setSavingCfg(false); }
    };
    const addOptout = async () => {
        if (!newContact.trim()) return;
        setAdding(true);
        try { await api.post("/whatsapp/optouts", { contact: newContact.trim() }); toast.success("Contact opted out"); setNewContact(""); await loadList(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not add"); }
        finally { setAdding(false); }
    };
    const resubscribe = async (contact) => {
        if (!window.confirm(`Re-subscribe ${contact}? Only do this if they consented again.`)) return;
        try { await api.post("/whatsapp/optouts/resubscribe", { contact }); toast.success("Re-subscribed"); await loadList(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not re-subscribe"); }
    };

    const stats = useMemo(() => {
        const l = list || [];
        return { total: l.length, customer: l.filter((o) => o.source !== "admin").length,
                 admin: l.filter((o) => o.source === "admin").length, recent: l.filter((o) => within7Days(o.opted_out_at)).length };
    }, [list]);
    const filtered = useMemo(() => (list || []).filter((o) => !q || (o.contact || "").includes(q.trim())), [list, q]);

    return (
        <div data-testid="wa-optouts" className="space-y-6">
            {/* header */}
            <div className="flex items-start gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-red-50 text-red-500"><Prohibit size={22} weight="duotone" /></div>
                <div>
                    <h2 className="font-heading text-2xl font-bold tracking-tight">Opt-outs &amp; compliance</h2>
                    <p className="mt-0.5 max-w-2xl text-sm text-stone2">Anyone who replies <b className="text-ink">STOP</b> is stored here permanently and skipped by the bot and every broadcast — until they opt back in.</p>
                </div>
            </div>

            {/* stats */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat testid="optout-stat-total" icon={Prohibit} value={stats.total} label="Total opted out" tint="bg-red-50 text-red-500" />
                <Stat testid="optout-stat-customer" icon={HandWaving} value={stats.customer} label="Replied STOP" tint="bg-amber-50 text-amber-600" />
                <Stat testid="optout-stat-admin" icon={User} value={stats.admin} label="Added by you" tint="bg-stone-100 text-stone-500" />
                <Stat testid="optout-stat-recent" icon={Clock} value={stats.recent} label="Last 7 days" tint="bg-emerald-50 text-emerald-600" />
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
                {/* list + manual add (main) */}
                <section className="lg:col-span-3 overflow-hidden rounded-2xl border border-stone-200 bg-white">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 px-5 py-3.5">
                        <div className="flex items-center gap-2 text-sm font-bold">Opted-out contacts <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-500">{list?.length ?? 0}</span></div>
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number" data-testid="optout-search"
                                    className="w-40 rounded-lg border border-stone-200 py-1.5 pl-8 pr-2 text-xs outline-none focus:border-emerald-400" />
                            </div>
                            <button onClick={loadList} title="Refresh" className="rounded-lg border border-stone-200 p-1.5 text-stone2 hover:bg-stone-50"><ArrowClockwise size={14} /></button>
                        </div>
                    </div>

                    {/* inline manual add */}
                    <div className="flex flex-wrap gap-2 border-b border-stone-100 bg-stone-50/60 px-5 py-3">
                        <input data-testid="optout-add-input" value={newContact} onChange={(e) => setNewContact(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addOptout()} placeholder="Manually opt out a number (e.g. 9812345678)"
                            className="flex-1 min-w-[200px] rounded-xl border border-stone-200 px-4 py-2 text-sm outline-none focus:border-emerald-400" />
                        <button data-testid="optout-add-btn" onClick={addOptout} disabled={adding}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-50">
                            {adding ? <CircleNotch size={15} className="animate-spin" /> : <PlusCircle size={15} weight="fill" />} Add
                        </button>
                    </div>

                    {list === null ? (
                        <p className="p-8 text-center text-sm text-stone2"><CircleNotch size={14} className="mr-1 inline animate-spin" /> Loading…</p>
                    ) : filtered.length === 0 ? (
                        <div className="px-6 py-14 text-center">
                            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-500"><ShieldCheck size={28} weight="duotone" /></div>
                            <p className="mt-4 font-semibold">{q ? "No matches" : "No opt-outs — you're all clear"}</p>
                            <p className="mt-1 text-sm text-stone2">{q ? "Try a different number." : "Contacts who reply STOP will appear here automatically."}</p>
                        </div>
                    ) : (
                        <ul data-testid="optout-list" className="divide-y divide-stone-100">
                            {filtered.map((o) => (
                                <li key={o.contact} className="flex items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-stone-50/60">
                                    <div className="flex items-center gap-3">
                                        <div className="grid h-9 w-9 place-items-center rounded-full bg-stone-100 text-stone-400"><User size={16} weight="duotone" /></div>
                                        <div>
                                            <div className="font-mono text-sm font-medium">{o.contact}</div>
                                            <div className="mt-0.5 flex items-center gap-2 text-xs text-stone2">
                                                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${o.source === "admin" ? "bg-stone-100 text-stone-500" : "bg-amber-50 text-amber-600"}`}>{o.source === "admin" ? "Manual" : "Replied STOP"}</span>
                                                {o.opted_out_at && <span>{new Date(o.opted_out_at).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <button data-testid={`optout-resub-${o.contact}`} onClick={() => resubscribe(o.contact)}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700">
                                        <ArrowClockwise size={13} /> Re-subscribe
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* SF sync config (side) */}
                <section className="lg:col-span-2 rounded-2xl border border-stone-200 bg-white p-5" data-testid="optout-sf-config">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-stone2">
                        <ShieldCheck size={14} weight="duotone" className="text-emerald-500" /> Reflect opt-outs in Salesforce
                    </div>
                    <p className="mt-1.5 text-xs text-stone2">Best-effort — matches the contact by phone. Needs Salesforce connected.</p>

                    <div className="mt-4 space-y-2">
                        {SF_MODES.map((m) => {
                            const active = mode === m.value;
                            const Icon = m.icon;
                            return (
                                <button key={m.value} data-testid={`optout-mode-${m.value}`} onClick={() => setMode(m.value)}
                                    className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-all ${active ? "border-emerald-400 bg-emerald-50/70 ring-1 ring-emerald-200" : "border-stone-200 hover:border-stone-300 hover:bg-stone-50"}`}>
                                    <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${active ? "bg-emerald-500 text-white" : "bg-stone-100 text-stone-500"}`}><Icon size={16} weight="duotone" /></div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 text-sm font-bold">{m.label}{active && <CheckCircle size={14} weight="fill" className="text-emerald-500" />}</div>
                                        <div className="mt-0.5 text-xs text-stone2">{m.hint}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {mode === "field" && (
                        <div className="mt-3">
                            <label className="text-xs font-semibold text-stone-600">Custom field API name (on Lead &amp; Contact)</label>
                            <input data-testid="optout-field-input" value={field} onChange={(e) => setField(e.target.value)}
                                placeholder="e.g. WhatsApp_OptOut__c"
                                className="mt-1 w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" />
                        </div>
                    )}

                    <button data-testid="optout-config-save" onClick={saveCfg} disabled={savingCfg}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-40">
                        {savingCfg ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} weight="fill" />} Save{mode !== savedMode ? " changes" : "d"}
                    </button>
                </section>
            </div>
        </div>
    );
}
